/**
 * DraftAskCard — one ask Merlin has DRAFTED but not sent.
 *
 * Owner ruling (Scott, review bz2bb6, 2026-10-04): "Merlin should be able to
 * suggest them, but not actually send them ... suggest actions and have a
 * press OK or change this or something." Nothing leaves the building until
 * Jess presses Send here; he can reword it first, or throw it away.
 *
 * A question Merlin addressed to several people (one broadcast group) is
 * one card: Send and Don't ask act on every copy (UX round 2026-10-20, the
 * drafts explorer had to OK each person's copy separately). Each press is
 * recorded (qcp_draft_send / qcp_draft_edit; qcp_ask_dismiss /
 * qcp_ask_dismiss_note for Don't ask, kanban 59a1ee04) with the draft ids
 * and where the card sits, never the typed words.
 */
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { trackEvent } from '../../services/portalTracking';
import {
  approveDataRequest,
  discardDataRequest,
  type DoorDataRequestRead,
} from '../../services/qcpDataRequests';
import { draftRecipients } from './asksView';
import { DismissControl } from './DismissControl';
import styles from './DraftAskCard.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface DraftAskCardProps {
  request: DoorDataRequestRead;
  /** The same question's drafts for the other people in its group. */
  others?: DoorDataRequestRead[];
  onRequestUpdated: (updated: DoorDataRequestRead) => void;
  /** Where the card sits, recorded with each press (surface, item number). */
  trackContext?: Record<string, string | number | null | undefined>;
}

export function DraftAskCard({ request, others = [], onRequestUpdated, trackContext = {} }: DraftAskCardProps) {
  // A read-only viewer sees the draft; only the desk sends it (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const rows = [request, ...others];
  const names = rows.map((r) => r.target_name);
  const firstNames = draftRecipients(names.map((n) => n.split(' ')[0]));
  const ids = rows.map((r) => r.id);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(request.question_title);
  const [body, setBody] = useState(request.question_body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One call per copy, in order; each answer is folded in as it lands, so a
  // copy that went before a failure shows as sent.
  const run = async (
    name: 'qcp_draft_send',
    action: (r: DoorDataRequestRead) => Promise<DoorDataRequestRead>,
  ) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    let done = 0;
    try {
      for (const r of rows) {
        if (r.status !== 'proposed') continue;
        onRequestUpdated(await action(r));
        done += 1;
      }
      trackEvent('action', name, { ...trackContext, data_request_ids: ids, edited: editing, result: 'saved' });
    } catch {
      trackEvent('action', name, { ...trackContext, data_request_ids: ids, edited: editing, done, result: 'failed' });
      setError('That did not go through. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const send = () =>
    void run('qcp_draft_send', (r) =>
      approveDataRequest(r.id, editing ? { question_title: title.trim(), question_body: body } : {}),
    );

  // Don't ask: every copy is thrown away with the same note (kanban
  // 59a1ee04); the control records the press and shows a refusal.
  const discardAll = async (note: string | null) => {
    if (busy) throw new Error('busy');
    setBusy(true);
    setError(null);
    try {
      for (const r of rows) {
        if (r.status !== 'proposed') continue;
        onRequestUpdated(await discardDataRequest(r.id, 'discarded by Jess', note));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    // id: where "Review and send" lands (revealDraft); focusable so the
    // reader's place moves with the scroll.
    <article className={styles.card} data-testid="draft-ask" id={`draft-${request.id}`} tabIndex={-1}>
      <div className={styles.head}>
        <span className={styles.badge}>Not sent yet</span>
        <span className={styles.who}>
          To <strong>{draftRecipients(names)}</strong>
        </span>
      </div>

      {editing ? (
        <>
          <label className={styles.label} htmlFor={`draft-title-${request.id}`}>
            What you are asking
          </label>
          <input
            id={`draft-title-${request.id}`}
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
          />
          <label className={styles.label} htmlFor={`draft-body-${request.id}`}>
            The message they get
          </label>
          <textarea
            id={`draft-body-${request.id}`}
            className={styles.textarea}
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={busy}
          />
        </>
      ) : (
        <>
          <p className={styles.title}>{request.question_title}</p>
          {/* Rendered as the recipient gets it (the email renders the
              markdown, and so does the card once it is sent): raw
              **double asterisks** here read as a sloppy message, and the
              sent card then looked like different words (UX round
              2026-10-06). The editor keeps the source text. */}
          <div className={styles.bodyMd} data-testid="draft-ask-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{request.question_body}</ReactMarkdown>
          </div>
        </>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {!readOnly && (<div className={styles.actions}>
        <button
          type="button"
          className={styles.send}
          onClick={send}
          disabled={busy || (editing && !title.trim())}
          data-action="qcp_draft_send"
        >
          Send it to {firstNames}
        </button>
        <button
          type="button"
          className={styles.ghost}
          onClick={() => {
            trackEvent('action', 'qcp_draft_edit', { ...trackContext, data_request_ids: ids, open: !editing });
            setEditing((v) => !v);
          }}
          disabled={busy}
        >
          {editing ? 'Stop editing' : 'Change it first'}
        </button>
        <DismissControl
          what={`the draft to ${draftRecipients(names)}`}
          label="Don't ask"
          noteLabel="Don't ask, say why"
          placeholder="Why not? e.g. Rich already asked the customer"
          onDismiss={discardAll}
          disabled={busy}
          track={{
            plain: 'qcp_ask_dismiss',
            note: 'qcp_ask_dismiss_note',
            form: 'qcp_draft_reason',
            targets: {
              thread_id: trackContext.thread_no,
              case_id: trackContext.investigation_no,
              data_request_ids: ids.join(','),
            },
            context: { ...trackContext, data_request_ids: ids, state: 'draft' },
          }}
        />
      </div>)}
    </article>
  );
}

export default DraftAskCard;
